import { EventEmitter } from '@angular/core';
import { sortAscend, newUuid, StringUtils, ObjectUtils, Url } from '@ark-libs/ngx-utils';

export enum NavMenuItemActionType{
	HREF = 'href',
	SREF = 'sref'
}
export class NavMenu {
	private static SEQ:string = "seq";

    uuid: string = newUuid();
	data: any;//[];
    activeOnClick:boolean = false;
	accordion:boolean = true;
	root: NavMenuItem;
	wholeTreeItems: NavMenuItem[];
	action: string;

	openChange: EventEmitter<NavMenuItem> = new EventEmitter();
	selectChange: EventEmitter<NavMenuItem> = new EventEmitter();

    constructor(options?: { data?: any }) {
    	this.patchOptions(options);
    	this.openChange.subscribe(menuItem=>this.onOpenChange(menuItem));
    	this.selectChange.subscribe(menuItem=>this.onSelectChange(menuItem));
    }

    private patchOptions(options?: any){
        if(!options) this.data = {};
    	if(!options.data) this.data = {children:[]};
		else if(Array.isArray(options.data))this.data={children:[...options.data]};
		else this.data = options.data;

		this.wholeTreeItems = [];
		this.root = new NavMenuItem(this, {data:this.data});
    }
    private onOpenChange(menuItem: NavMenuItem){
    	if(menuItem.opened) this.openTo(menuItem);
    }
    private onSelectChange(menuItem: NavMenuItem){
    	if(menuItem.selected){
    		this.wholeTreeItems
    			.filter(item=>item.selected&&item.uuid!=menuItem.uuid)
    			.forEach(item=> {item._selected = false;});
    	}
    }
    public select(menuItem?: NavMenuItem, event?:any){
        if(menuItem==null){
            this.wholeTreeItems.forEach(item=>item.selected=false);
        }else if(this.activeOnClick||menuItem.action){
	    	menuItem.selected = true;
	    }
    }
    public openToSelected(){
    	this.openTo(this.wholeTreeItems.filter(item=>item.selected).shift(),true);
    }
    public openTo(menuItem: NavMenuItem, force?:boolean){
		let ancestors: NavMenuItem[] = [];
		let parent = menuItem;
		while(parent){
			if(force) parent._opened = true;
			ancestors.push(parent);
			parent = parent.parent;
		}
        if(this.accordion){
    		this.wholeTreeItems.filter(item=>item._opened).forEach(item=>{
    			if(!ancestors.some(ancestor=>ancestor.uuid === item.uuid)){
    				item._opened = false;
    			}
    		});
        }
	}
	get items(): NavMenuItem[]{
		return this.root?this.root.children:[];
	}

    findItemByUrl(url:string): NavMenuItem{
        return this.wholeTreeItems.find(item=>item.isMatchedUrl(url));
    }

    pathFromRootByUrl(url: string): NavMenuItem[]{
        let menuItem;
        while(!menuItem){
            menuItem = this.findItemByUrl(url);
            if(menuItem||url.lastIndexOf("/")<=0||url.indexOf("http")==0) break;
            url = url.substring(0, url.lastIndexOf("/"));
        }

        if(menuItem == null) return [];
        let parent = menuItem;
        let paths = [];
        const maxLevel = 20;
        while(parent){
			if(parent == this.root) break;
            paths.unshift(parent);
            if(paths.length>maxLevel) break;
            parent = parent.parent;
        }
        return paths;
    }

}
export class NavMenuItem {
	data: any;

	uuid: string = newUuid();
	parentUuid: string;
	menu: NavMenu;
	parent: NavMenuItem;
	children: NavMenuItem[];

	seq: number;
	name: string;
	title: string;
	icon: string;
	actionType: NavMenuItemActionType;
	_action: string;
	visible: boolean;
	_opened: boolean;
	_selected: boolean;
	_baseRouteUrl: string;

    constructor(menu:NavMenu, options?: { parent?:NavMenuItem, data?: any }) {
        this.menu = menu;
    	this.patchOptions(options);
    	this.menu.wholeTreeItems.push(this);
    }

    private patchOptions(options?: any){
    	if(!options) options = {};

    	const data = options.data||{};
    	this.parent = options.parent;
    	this.parentUuid = this.parent?this.parent.uuid:null;
		this.seq = data.seq;
		this.name = data.name;
    	this.title = data.label||data.title||data.text||data.name;
    	this.icon = data.icon||'anticon anticon-link';
		this.visible = data.invisible!=null?!data.invisible:!!data.visible;

		this.actionType = StringUtils.isEmpty(data.action)?null: (data.actionType?data.actionType.toLowerCase():NavMenuItemActionType.SREF);
		this._action = data.action?data.action.replace(/\$*\{*localhost\}*|\$*\{host\}/ig, self.location.hostname):null;
		this._action = ObjectUtils.resolveParamRef(this._action, data);

    	this._opened = !!data.opened;
    	this._selected = !!data.selected;

    	this.data = data;

    	this.children = [];
		let children = data.children||data.items||[];
    	if(!Array.isArray(children)) children = [children];
    	children.sort((a,b)=>sortAscend(a.seq,b.seq)).forEach(child=>{
    		this.children.push(new NavMenuItem(this.menu, {parent: this, data:child}));
    	});
	}
	
	get baseRouteUrl(): string{
		if(this._baseRouteUrl) return this._baseRouteUrl;
		return this.parent?(this.parent._baseRouteUrl||this.parent.getRouterLinkRelativePath()||this.parent.baseRouteUrl):null;
	}
	set baseRouteUrl(routerUrl:string){
		if(routerUrl){
			routerUrl = routerUrl.substring(routerUrl.indexOf('#')+1,routerUrl.length);
			if(routerUrl.indexOf("#")!=-1) routerUrl = routerUrl.substring(0,routerUrl.indexOf('#'));
			if(routerUrl.indexOf("?")!=-1) routerUrl = routerUrl.substring(0,routerUrl.indexOf('?'));
		}
		this._baseRouteUrl = routerUrl;
	}

	public get routerLink(): string{
		if(this.actionType==NavMenuItemActionType.HREF||!this._action) return null;
		const link = this.getRouterLinkRelativePath();
		return (link==null||link.indexOf("?")==-1)?link:link.substring(0,link.indexOf('?'));
	}
	public get queryParams() : any {
		if(!this._action||this._action.indexOf("?")==-1) return null;
		return Url.relative(this._action).query;
	}
	
	/**
	 * 根据上级路由地址获取当前节点的路由
	 */
	private getRouterLinkRelativePath():string{
		let selfLink = this.getSelfRouterLink();
		if(selfLink&&selfLink.startsWith('/')) return selfLink;

		let parentLink = this._baseRouteUrl;
		if(!parentLink && this.parent){
			if (this.parent.menu && this.parent.menu.action) {
				if (this.parent.action) {
					parentLink = this.parent.action
				} else {
					if (this.parent.parent && this.parent.parent.action) {
						parentLink = this.parent.menu.action + '/' + this.parent.parent.action;
					} else {
						parentLink = this.parent.menu.action
					}
				}
			} else {
				parentLink = this.parent.getRouterLinkRelativePath();
			}
		}
		parentLink = parentLink||"";

		while(selfLink&&selfLink.startsWith('../')){
			if(parentLink.endsWith('../')) break;
			selfLink = selfLink.substring(3, selfLink.length);

			if(parentLink.endsWith('/')) parentLink = parentLink.substring(0,parentLink.length-1);
			if(parentLink.indexOf('/')==-1) parentLink = "../";
			else parentLink = parentLink.substring(0, parentLink.lastIndexOf('/'));
		}
		return parentLink+(parentLink.endsWith('/')?'':"/")+(selfLink||'');
	}
	/**
	 * 获取当前节点自己的路由地址设置
	 */
	private getSelfRouterLink():string{
		if(!this._action) return null;

		let action = this._action;
		if(this.actionType==NavMenuItemActionType.SREF) return action;

		// 只有根节点才分析#字符
		if(action.indexOf('#')==-1||this!=this.menu.root) return null;

		action = action.substring(action.indexOf('#')+1,action.length);
		if(action.indexOf("#")!=-1) action = action.substring(0,action.indexOf('#'));
		if(action.indexOf("?")!=-1) action = action.substring(0,action.indexOf('?'));
		return action;
	}

	
	public get action() : string {
		if(this.actionType!=NavMenuItemActionType.HREF) return this.routerLink;
		return this._action;
	}
	
	public set action(v : string) {
		this._action = v;
	}
	
	

    public get hasChildren() : boolean {
    	return this.children.length>0;
    }

    public get opened() : boolean {
    	return this._opened;
    }
    public set opened(v : boolean) {
    	if(this._opened != v){
	    	this._opened = v;
	    	this.menu.openChange.emit(this);
	    }
    }

    public get selected() : boolean {
    	return this._selected;
    }
    public set selected(v : boolean) {
    	if(this._selected != v){
	    	this._selected = v;
	    	this.menu.selectChange.emit(this);
	    }
	}
	
	get isRoot(){
		return this==this.menu.root;
	}

    isMatchedUrl(url:string): boolean{
		const link = this.routerLink;

		if(url==null||url==undefined||url.length==0||link==null) return false;
		if(link==url) return true;
		
		if(!(link.startsWith(url)||url.startsWith(link))) return false;

		let moreChars = (link.length>url.length)?link.substring(url.length):url.substring(link.length);
		if(moreChars.startsWith("/")) moreChars = moreChars.substring(1);
		
        let isMatch = moreChars.length==0||moreChars.startsWith("?")||moreChars.startsWith("#");
        // console.log("action="+link, "url="+url, "isMatch="+isMatch);
        return isMatch;
    }
}
